import axios from 'axios';
import dynamic from 'next/dynamic';
import { useEffect, useState, useRef, Suspense } from 'react';
import Link from 'next/link';
import { isMobile } from 'react-device-detect';
import { useObjectsDispatch } from '../../context/objects';
import Skelet from '../Skelet';
import ObjectSimilarSkeletonItem from './ObjectSimilarSkeletonItem';
import styles from '../../styles/modules/Carousel.module.scss';

const categoryToLink = (c) => {
  switch (c) {
    default:
    case 'квартира':
      return 'flats';

    case 'комната':
      return 'rooms';

    case 'дом':
      return 'houses';

    case 'участок':
      return 'sites';

    case 'коммерция':
      return 'commercial';
  }
};
const flatsToLink = (f) => {
  switch (f) {
    case 'студия':
      return 'studio';

    default:
    case 1:
      return '1k';

    case 2:
      return '2k';

    case 3:
      return '3k';

    case 4:
      return '4k';
  }
};

const ObjectSimilarItem = dynamic(() => import('./ObjectSimilarItem'), { suspense: true });

const ObjectSimilar = ({ id, object }) => {
  const setObjectsState = useObjectsDispatch();
  const [carouselScrollPosition, setCarouselScrollPosition] = useState(0);
  const [carouselScrollWidth, setCarouselScrollWidth] = useState(0);
  const [carouselElWidth, setCarouselElWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const carouselContentEl = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          process.env.NEXT_PUBLIC_API + `/object/${id}/similar?length=9`
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setData(response.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        setIsLoading(false);
        setIsError(true);
      }
    };
    fetchData();
  }, []);

  const handlePreviousClick = () => {
    if (!carouselContentEl.current) return;

    carouselContentEl.current.scrollTo({
      left: carouselContentEl.current.scrollLeft - carouselContentEl.current.offsetWidth,
      behavior: 'smooth'
    });

    setCarouselScrollPosition(
      carouselContentEl.current.scrollLeft - carouselContentEl.current.offsetWidth
    );
    setCarouselScrollWidth(carouselContentEl.current.scrollWidth);
    setCarouselElWidth(carouselContentEl.current.offsetWidth);
  };
  const handleNextClick = () => {
    if (!carouselContentEl.current) return;

    carouselContentEl.current.scrollTo({
      left: carouselContentEl.current.scrollLeft + carouselContentEl.current.offsetWidth,
      behavior: 'smooth'
    });

    setCarouselScrollPosition(
      carouselContentEl.current.scrollLeft + carouselContentEl.current.offsetWidth
    );
    setCarouselScrollWidth(carouselContentEl.current.scrollWidth);
    setCarouselElWidth(carouselContentEl.current.offsetWidth);
  };
  const handleSearchBackClick = () => {
    const obj = {};

    obj.type = object.type;
    obj.category = object.category;

    if (object.category === 'квартира') {
      obj.studio = object.object.studio;
      obj.rooms = [object.object.rooms.toString()];
    }

    setObjectsState({
      type: 'SET_FILTER',
      payload: obj
    });
  };
  const getLink = () => {
    const arr = ['/objects'];
    arr.push(object.type === 'аренда' ? 'rent' : 'buy');
    arr.push(categoryToLink(object.category));

    if (object.category === 'квартира')
      arr.push(flatsToLink(object.object.studio ? 'студия' : object.object.rooms));

    return arr.join('/');
  };

  if (isLoading || isError)
    return (
      <section className="block crop">
        <div className="fixed">
          <h3>
            <Skelet style={{ maxWidth: 400, width: '100%', height: 47.5 }} />
          </h3>
          <div className={styles.carousel}>
            <div className={styles.carouselContent}>
              {[...Array(6).keys()].map((value) => (
                <ObjectSimilarSkeletonItem key={value} />
              ))}
            </div>
          </div>
        </div>
      </section>
    );

  return !!data.length ? (
    <section className="block crop">
      <div className="fixed">
        <h3>Похожие объекты</h3>
        <div className={styles.carousel}>
          {carouselScrollPosition > 0 && (
            <button
              className={[styles.carouselButton, styles.carouselButtonPrevious].join(' ')}
              onClick={handlePreviousClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <div
            className={[styles.carouselContent, isMobile && styles.touch].filter(Boolean).join(' ')}
            ref={carouselContentEl}
          >
            {data.map((value, index) => (
              <Suspense fallback={<ObjectSimilarSkeletonItem />} key={index}>
                <ObjectSimilarItem item={value} />
              </Suspense>
            ))}
            <Link
              href={getLink()}
              passHref
              className={styles.carouselItemMore}
              type="button"
              onClick={handleSearchBackClick}
            >
              <span>Вернуться к поиску объектов</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100"
                height="100"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
            <div className={styles.carouselItemDummy} />
          </div>
          {carouselScrollPosition + carouselElWidth <= carouselScrollWidth && data.length >= 4 && (
            <button
              className={[styles.carouselButton, styles.carouselButtonNext].join(' ')}
              onClick={handleNextClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </section>
  ) : null;
};

export default ObjectSimilar;
